import { useTranslation } from "react-i18next";
import { useAvailableCities } from "../../../store/adminDataStore";
import { CITY_CONFIG } from "../../../config/cities";

interface CityStepProps {
  selected: string;
  onSelect: (city: string) => void;
  onNext: () => void;
}

export default function CityStep({ selected, onSelect, onNext }: CityStepProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.split("-")[0] ?? "en") as "zh" | "en" | "ja";
  // Derive available cities from active services.city_scope — no MOCK_CITIES
  const availableCities = useAvailableCities();

  return (
    <div className="w-full max-w-4xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-extralight text-[#2C2C2C] mb-3" style={{ letterSpacing: "-0.02em" }}>
          {t("tabifoto.booking.select_city")}
        </h2>
        <div className="w-8 h-px bg-[#C8B8A6] mx-auto" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CITY_CONFIG.map((city) => {
          const isAvailable = availableCities.has(city.id);
          return (
            <div
              key={city.id}
              onClick={() => isAvailable && onSelect(city.id)}
              className={`relative overflow-hidden rounded-lg transition-all duration-300 ${
                isAvailable ? "cursor-pointer" : "cursor-default opacity-50"
              } ${selected === city.id ? "ring-2 ring-[#C8B8A6]" : ""}`}
              style={{ aspectRatio: "3/4" }}
            >
              <img
                src={city.image}
                alt={city.name[lang]}
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

              {selected === city.id && (
                <div className="absolute inset-0 bg-[#C8B8A6]/20" />
              )}

              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="text-xl font-light text-white">{city.name[lang]}</h3>
                {!isAvailable && (
                  <span className="mt-1 inline-block text-xs uppercase tracking-widest text-white/40 border border-white/20 px-2 py-0.5 rounded-full">
                    {t("tabifoto.cities.coming_soon")}
                  </span>
                )}
              </div>

              {selected === city.id && (
                <div className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center rounded-full bg-[#C8B8A6]">
                  <i className="ri-check-line text-white text-xs" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end mt-10">
        <button
          onClick={onNext}
          disabled={!selected}
          className="px-8 py-3 rounded-full bg-[#2C2C2C] text-white text-sm tracking-widest uppercase hover:bg-[#2C2C2C]/80 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
        >
          {t("tabifoto.booking.next")} →
        </button>
      </div>
    </div>
  );
}
